"use client";

import Link from "next/link";
import { BookOpen, ChevronRight, FileText } from "lucide-react";

import SabineAvatar from "@/components/support/SabineAvatar";
import {
  CATEGORY_TONES,
  SUPPORT_CATEGORIES,
} from "@/components/support/support-categories";
import { getHelpCenterLink } from "@/lib/support/help-audience";

// Both footer links, so the pair cannot drift apart.
const FOOTER_LINK_CLASS =
  "inline-flex items-center gap-1.5 text-xs text-gray-400 transition-colors hover:text-warning-500";

/**
 * The support dialog's first screen: Sabine's hello, and what the message can
 * be about. Picking a category opens the compose screen (`SupportDialog`).
 *
 * Its footer offers the two ways out of filing a ticket: the Help Center for
 * this reader's role, and the tickets they have already filed.
 *
 * @param {{ onSelect: (category: object) => void, onClose: () => void, role?: string }} props
 */
export default function SupportWelcome({ onSelect, onClose, role }) {
  const helpCenter = getHelpCenterLink(role);

  return (
    <div className="space-y-4 py-1">
      {/* Sabine Hero Introduction */}
      <div className="relative overflow-hidden rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-br from-warning-500/10 via-warning-400/5 to-transparent pointer-events-none" />
        <div className="relative flex items-start gap-3.5 p-4 rounded-2xl border border-warning-100 bg-gradient-to-br from-warning-50/80 to-white">
          <SabineAvatar size="hero" />

          {/* Intro Text */}
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-[15px] font-bold text-gray-900 tracking-tight">
                Hey, I'm Sabine 👋
              </span>
            </div>
            <p className="text-sm text-gray-600 leading-snug">
              Tell me what's going on — I'll help you get it sorted out quickly.
            </p>
          </div>
        </div>
      </div>

      {/* Section Label */}
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest px-0.5">
        What do you need?
      </p>

      {/* Category Options */}
      <div className="space-y-2">
        {SUPPORT_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const c = CATEGORY_TONES[cat.color];

          return (
            <button
              key={cat.id}
              onClick={() => onSelect(cat)}
              className="group w-full flex items-center gap-3.5 p-3.5 rounded-xl border border-gray-100 bg-white hover:border-warning-200 hover:bg-warning-50/30 hover:shadow-sm transition-all duration-200 text-left"
            >
              <div
                className={`flex-shrink-0 w-10 h-10 rounded-xl ${c.bg} ${c.hoverBg} flex items-center justify-center transition-all duration-200 group-hover:scale-105`}
              >
                <Icon className={`w-5 h-5 ${c.icon}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800">
                  {cat.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {cat.description}
                </div>
              </div>
              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 translate-x-1 group-hover:translate-x-0 transition-all duration-200">
                <div className="w-6 h-6 rounded-full bg-warning-100 flex items-center justify-center">
                  <ChevronRight
                    className="w-3 h-3 text-warning-500"
                    strokeWidth={2.5}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer: read the guides, or look at what you have already asked. */}
      <div className="flex items-center justify-center gap-3 pt-1 pb-0.5">
        <Link
          href={helpCenter.href}
          onClick={onClose}
          className={FOOTER_LINK_CLASS}
        >
          <BookOpen className="w-3.5 h-3.5" />
          {helpCenter.label}
        </Link>
        <span aria-hidden="true" className="h-3 w-px bg-gray-200" />
        <Link
          href="/support/requests"
          onClick={onClose}
          className={FOOTER_LINK_CLASS}
        >
          <FileText className="w-3.5 h-3.5" />
          Previous requests
        </Link>
      </div>
    </div>
  );
}
